<template>
  <div
    class="d-flex justify-center align-center gradient-background"
    style="min-height: 100%;"
  >
    <v-card
      class="d-flex flex-column align-center pa-5"
      elevation="10"
      style="width: 100%; max-width: 480px; border: 1px solid var(--v-foreground2-base);"
    >
      <h1 class="mb-1">{{ env.longName }}</h1>
      <h3 class="mb-4 font-weight-regular">Email Preferences</h3>

      <div
        v-if="state === 'loading'"
        class="d-flex justify-center align-center py-6"
      >
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div
        v-else-if="state === 'error'"
        class="text-center py-4"
      >
        <v-icon color="error" large class="mb-2">mdi-alert-circle-outline</v-icon>
        <p class="mb-0">{{ errorMessage }}</p>
      </div>

      <div
        v-else-if="state === 'success'"
        style="width: 100%;"
      >
        <p class="text-left mb-1">
          Manage your email subscriptions below. Changes are saved automatically.
        </p>

        <v-divider class="mb-3" />

        <div v-if="emailLists.length === 0" class="text-center py-4 grey--text">
          No email lists available.
        </div>

        <v-checkbox
          v-for="(list, i) of emailLists"
          :key="list.id"
          v-model="emailLists[i].value"
          :label="emailLists[i].name"
          :loading="saving[list.id]"
          :disabled="saving[list.id]"
          hide-details
          class="my-2"
          @change="saveEmailOptIn(list.id, i)"
        />

        <v-slide-y-transition>
          <v-alert
            v-if="saveMessage"
            type="success"
            dense
            text
            class="mt-4 mb-0"
          >
            {{ saveMessage }}
          </v-alert>
        </v-slide-y-transition>
      </div>
    </v-card>
  </div>
</template>

<script>
import fetchV2 from '../assets/js/fetchV2';

export default {
  name: 'Unsubscribe',

  props: {
    env: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    state: 'loading',
    errorMessage: '',
    emailLists: [],
    saving: {},
    saveMessage: '',
    saveTimer: null,
  }),

  computed: {
    jwt() {
      return this.$route.query.jwt || '';
    },
    authHeaders() {
      return { Authorization: 'Bearer ' + this.jwt };
    },
  },

  methods: {
    async loadEmailLists() {
      if (!this.jwt) {
        this.errorMessage = 'Missing or invalid unsubscribe token. Please use the link from your email.';
        this.state = 'error';
        return;
      }

      try {
        const [emailLists, optIns] = await Promise.all([
          fetchV2('GET /email_list/personal', {}, this.authHeaders),
          fetchV2('GET /email_list_x_user/personal', {}, this.authHeaders),
        ]);

        emailLists.forEach((list) => {
          list.value = optIns.some((o) => o.email_list_id === list.id);
        });

        this.emailLists = emailLists;
        this.state = 'success';
      } catch (err) {
        console.error(err);
        this.errorMessage = 'Unable to load your email preferences. The link may be expired or invalid.';
        this.state = 'error';
      }
    },

    saveEmailOptIn(id, index) {
      this.$set(this.saving, id, true);

      fetchV2('PUT /email_list_x_user/personal', { email_list_id: id }, this.authHeaders)
        .then(() => {
          this.showSaveMessage('Your email preferences have been updated.');
        })
        .catch((err) => {
          console.error(err);
          this.emailLists[index].value = !this.emailLists[index].value;
          this.showSaveMessage('An error occurred. Please try again.');
        })
        .finally(() => {
          this.$set(this.saving, id, false);
        });
    },

    showSaveMessage(msg) {
      this.saveMessage = msg;
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        this.saveMessage = '';
      }, 3000);
    },
  },

  created() {
    this.loadEmailLists();
  },

  beforeDestroy() {
    clearTimeout(this.saveTimer);
  },
};
</script>

<style scoped>
.gradient-background {
  background-image: linear-gradient(270deg, var(--v-gray-base) 0%, var(--v-primary-base) 100%);
}
</style>
